/**
 * useResources hook
 * Fetches and caches the resource list from the API.
 */

import { useState, useEffect, useCallback } from 'react';
import { getResources, getResourceCategories, getResourceById } from '../../../../services/oathResourceService';

/**
 * Hook to fetch the resource list with optional filters.
 * Accepts all params supported by GET /oath/resources.
 *
 * @param {Object} params
 * @param {string} [params.type] - 'video' | 'guide'
 * @param {number} [params.categoryId]
 * @param {string} [params.pageContext] - route slug for contextual filtering e.g. 'power-reports'
 * @param {string} [params.search]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=25]
 */
export const useResources = ({ type, categoryId, pageContext, search, page, pageSize } = {}) => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [resourceResult, categoryResult] = await Promise.all([
      getResources({ type, categoryId, pageContext, search, page, pageSize }),
      getResourceCategories(),
    ]);

    if (resourceResult.success) {
      setResources(
        resourceResult.data.map((r) => ({
          ...r,
          createdAt: r.createdAt || r.dateCreated || r.created || r.createDate,
        }))
      );
    } else {
      setResources([]);
      setError(resourceResult.error || 'Failed to load resources');
    }

    if (categoryResult.success) {
      setCategories(categoryResult.categories);
    } else {
      setCategories([]);
    }

    setLoading(false);
  }, [type, categoryId, pageContext, search, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { resources, categories, loading, error, refetch: fetchAll };
};

/**
 * Hook to fetch a single resource's full detail including guide body.
 * Calls GET /oath/resources/{resourceId}
 */
export const useResourceDetail = (resourceId) => {
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    setError(null);

    const result = await getResourceById(resourceId);
    if (result.success) {
      setResource(result.resource);
    } else {
      setError(result.error);
    }
    setLoading(false);
  }, [resourceId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return { resource, loading, error };
};
